<template>
  <div
    ref="containerRef"
    class="absolute inset-0 touch-none overflow-hidden"
    @pointerdown="handlePointerDown"
    @pointermove="handlePointerMove"
    @pointerup="handlePointerUp"
    @pointercancel="handlePointerUp"
    @wheel.prevent="handleWheel"
  >
    <div
      class="absolute left-0 top-0 origin-top-left"
      :class="{ 'transition-transform duration-200': isAnimated }"
      :style="{
        width: `${contentSize.width}px`,
        height: `${contentSize.height}px`,
        transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
      }"
    >
      <img
        :src="src"
        alt=""
        draggable="false"
        class="h-full w-full object-cover"
        @load="handleImageLoad"
      />
      <!-- Drawn over the image and zoomed and panned with it. -->
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useElementSize } from '@vueuse/core';

const props = defineProps<{
  src: string;
  /** Shows the whole photo, letterboxed, instead of filling the frame; zooming in can then use the whole frame. */
  contain?: boolean;
  /** One finger draws instead of panning; two fingers still zoom and pan. */
  oneFingerDraws?: boolean;
}>();

/** A drawing point in the unzoomed photo frame, with that frame's size. */
export type FramePoint = { x: number; y: number; frameWidth: number; frameHeight: number };

const emit = defineEmits<{
  (e: 'drawStart', point: FramePoint): void;
  (e: 'drawMove', point: FramePoint): void;
  (e: 'drawEnd'): void;
  /** A second finger turned the stroke into a pinch; drop what was drawn of it. */
  (e: 'drawCancel'): void;
}>();

/** Current zoom factor (1 = fit), so callers can fade overlays drawn for the unzoomed frame. */
const scale = defineModel<number>('scale', { default: 1 });

const MAX_SCALE = 5;
const DOUBLE_TAP_SCALE = 3;
const DOUBLE_TAP_MS = 300;
const TAP_SLOP_PX = 10;
/** Released below this zoom, the image springs back to fit. */
const SNAP_BACK_SCALE = 1.25;

const containerRef = ref<HTMLElement | null>(null);
const offsetX = ref(0);
const offsetY = ref(0);
/** Only programmatic zooms (double tap) animate; fingers and wheel move the image directly. */
const isAnimated = ref(false);
const imageAspect = ref(0);
const { width: containerWidth, height: containerHeight } = useElementSize(containerRef);

/** Unzoomed size of the photo: the whole frame, or the photo fitted into it with `contain`. */
const contentSize = computed(() => {
  const frameWidth = containerWidth.value;
  const frameHeight = containerHeight.value;
  if (!props.contain || !imageAspect.value) return { width: frameWidth, height: frameHeight };
  const width = Math.min(frameWidth, frameHeight * imageAspect.value);
  return { width, height: width / imageAspect.value };
});
const pointers = reactive(new Map<number, { x: number; y: number }>());

let lastPinch: { distance: number; midX: number; midY: number } | null = null;
let panStart: { x: number; y: number; offsetX: number; offsetY: number } | null = null;
let tapStart: { x: number; y: number } | null = null;
let lastTap: { x: number; y: number; time: number } | null = null;
let isDrawingStroke = false;

const getLocalPoint = (event: PointerEvent | WheelEvent) => {
  const rect = containerRef.value!.getBoundingClientRect();
  return { x: event.clientX - rect.left, y: event.clientY - rect.top };
};

/** Where a container point lies in the unzoomed frame, so a stroke follows the finger at any zoom. */
const toFramePoint = (point: { x: number; y: number }): FramePoint => ({
  x: (point.x - offsetX.value) / scale.value,
  y: (point.y - offsetY.value) / scale.value,
  frameWidth: contentSize.value.width,
  frameHeight: contentSize.value.height,
});

/** Scales around a point of the container, keeping that image point under it, within bounds. */
const applyZoom = (
  nextScale: number,
  pointX: number,
  pointY: number,
  fromOffsetX = offsetX.value,
  fromOffsetY = offsetY.value,
) => {
  const clampedScale = Math.min(MAX_SCALE, Math.max(1, nextScale));
  const contentX = (pointX - fromOffsetX) / scale.value;
  const contentY = (pointY - fromOffsetY) / scale.value;
  scale.value = clampedScale;
  applyOffset(pointX - contentX * clampedScale, pointY - contentY * clampedScale);
};

/** Centres the photo along an axis where it is smaller than the frame, else keeps the frame covered. */
const clampOffset = (offset: number, frame: number, content: number) => {
  const scaled = content * scale.value;
  if (scaled <= frame) return (frame - scaled) / 2;
  return Math.min(0, Math.max(frame - scaled, offset));
};

const applyOffset = (x: number, y: number) => {
  offsetX.value = clampOffset(x, containerWidth.value, contentSize.value.width);
  offsetY.value = clampOffset(y, containerHeight.value, contentSize.value.height);
};

const handleImageLoad = (event: Event) => {
  const image = event.target as HTMLImageElement;
  imageAspect.value = image.naturalHeight ? image.naturalWidth / image.naturalHeight : 0;
};

const getPinch = () => {
  const [a, b] = [...pointers.values()];
  return {
    distance: Math.hypot(a.x - b.x, a.y - b.y),
    midX: (a.x + b.x) / 2,
    midY: (a.y + b.y) / 2,
  };
};

const handlePointerDown = (event: PointerEvent) => {
  isAnimated.value = false;
  containerRef.value?.setPointerCapture(event.pointerId);
  const point = getLocalPoint(event);
  pointers.set(event.pointerId, point);
  if (pointers.size === 2) {
    if (isDrawingStroke) {
      isDrawingStroke = false;
      emit('drawCancel');
    }
    lastPinch = getPinch();
    panStart = null;
    tapStart = null;
  } else if (pointers.size === 1) {
    if (props.oneFingerDraws) {
      isDrawingStroke = true;
      emit('drawStart', toFramePoint(point));
      return;
    }
    panStart = { ...point, offsetX: offsetX.value, offsetY: offsetY.value };
    tapStart = point;
  }
};

const handlePointerMove = (event: PointerEvent) => {
  if (!pointers.has(event.pointerId)) return;
  const point = getLocalPoint(event);
  pointers.set(event.pointerId, point);
  if (isDrawingStroke) {
    emit('drawMove', toFramePoint(point));
    return;
  }
  if (tapStart && Math.hypot(point.x - tapStart.x, point.y - tapStart.y) > TAP_SLOP_PX) {
    tapStart = null;
  }
  if (lastPinch && pointers.size === 2) {
    // Follow the fingers' midpoint, then scale around it by how far they spread since last move.
    const pinch = getPinch();
    applyZoom(
      (scale.value * pinch.distance) / lastPinch.distance,
      pinch.midX,
      pinch.midY,
      offsetX.value + pinch.midX - lastPinch.midX,
      offsetY.value + pinch.midY - lastPinch.midY,
    );
    lastPinch = pinch;
  } else if (panStart && scale.value > 1) {
    applyOffset(panStart.offsetX + point.x - panStart.x, panStart.offsetY + point.y - panStart.y);
  }
};

const handlePointerUp = (event: PointerEvent) => {
  pointers.delete(event.pointerId);
  if (isDrawingStroke) {
    isDrawingStroke = false;
    emit('drawEnd');
  }
  if (pointers.size < 2) lastPinch = null;
  if (pointers.size === 1) {
    const [point] = [...pointers.values()];
    panStart = { ...point, offsetX: offsetX.value, offsetY: offsetY.value };
  }
  if (pointers.size > 0) return;
  panStart = null;
  // Barely zoomed reads as "not zoomed" (and blocks swiping in galleries): settle back to fit.
  if (scale.value > 1 && scale.value < SNAP_BACK_SCALE) {
    isAnimated.value = true;
    applyZoom(1, 0, 0);
  }
  if (!tapStart) return;
  const now = performance.now();
  if (lastTap && now - lastTap.time < DOUBLE_TAP_MS) {
    isAnimated.value = true;
    if (scale.value > 1) applyZoom(1, 0, 0);
    else applyZoom(DOUBLE_TAP_SCALE, tapStart.x, tapStart.y);
    lastTap = null;
  } else {
    lastTap = { ...tapStart, time: now };
  }
  tapStart = null;
};

const handleWheel = (event: WheelEvent) => {
  isAnimated.value = false;
  const point = getLocalPoint(event);
  applyZoom(scale.value * Math.exp(-event.deltaY / 300), point.x, point.y);
};

watch(
  () => props.src,
  () => {
    isAnimated.value = false;
    scale.value = 1;
    applyOffset(0, 0);
  },
);

/** Re-centres when the caller resets the zoom, e.g. a zoomed photo becoming a gallery neighbour. */
watch(scale, () => applyOffset(offsetX.value, offsetY.value));

/** Keeps the photo in bounds when the frame resizes (rotation) or the photo's size is known. */
watch(contentSize, () => applyOffset(offsetX.value, offsetY.value));
</script>
